import PropTypes from 'prop-types';
import React from 'react';
import {compose} from 'redux';
import {connect} from 'react-redux';
import ReactModal from 'react-modal';
import VM from 'scratch-vm';
import {injectIntl, intlShape} from 'react-intl';

import ErrorBoundaryHOC from '../lib/error-boundary-hoc.jsx';
import {
    getIsError,
    getIsShowingProject
} from '../reducers/project-state';
import {
    activateTab,
    BLOCKS_TAB_INDEX,
    COSTUMES_TAB_INDEX,
    SOUNDS_TAB_INDEX
} from '../reducers/editor-tab';

import {
    closeCostumeLibrary,
    closeBackdropLibrary,
    closeTelemetryModal,
    openExtensionLibrary
} from '../reducers/modals';

import FontLoaderHOC from '../lib/font-loader-hoc.jsx';
import LocalizationHOC from '../lib/localization-hoc.jsx';
import SBFileUploaderHOC from '../lib/sb-file-uploader-hoc.jsx';
import ProjectFetcherHOC from '../lib/project-fetcher-hoc.jsx';
import TitledHOC from '../lib/titled-hoc.jsx';
import ProjectSaverHOC from '../lib/project-saver-hoc.jsx';
import QueryParserHOC from '../lib/query-parser-hoc.jsx';
import storage from '../lib/storage';
import {callAndroidInterfaceMethod, hasAndroidInterfaceMethod} from '../lib/app-interface.js';
import {safeLocalStorageGet, safeLocalStorageSet} from '../lib/web-storage.js';
import vmListenerHOC from '../lib/vm-listener-hoc.jsx';
import vmManagerHOC from '../lib/vm-manager-hoc.jsx';
import cloudManagerHOC from '../lib/cloud-manager-hoc.jsx';
import systemPreferencesHOC from '../lib/system-preferences-hoc.jsx';

import GUIComponent from '../components/gui/gui.jsx';
import {setIsScratchDesktop} from '../lib/isScratchDesktop.js';


// import {
//     changeMonskitModuleList
// } from '../reducers/inserted_list';

const {RequestMetadata, setMetadata, unsetMetadata} = storage.scratchFetch;

const setProjectIdMetadata = projectId => {
    // If project ID is '0' or zero, it's not a real project ID. In that case, remove the project ID metadata.
    // Same if it's null undefined.
    if (projectId && projectId !== '0') {
        setMetadata(RequestMetadata.ProjectId, projectId);
    } else {
        unsetMetadata(RequestMetadata.ProjectId);
    }
};

class GUI extends React.Component {
    applyModuleSpriteVisibility () {
        const module = new URLSearchParams(window.location.search).get('module') || 'default';
        const shouldShowDefaultSprite = module === 'xcoding';
        const spriteTargets = this.props.vm.runtime.targets.filter(target => !target.isStage && target.isOriginal);

        spriteTargets.forEach(target => {
            if (typeof target.setVisible === 'function') {
                target.setVisible(shouldShowDefaultSprite);
            }
        });

        if (this.props.vm.renderer) {
            this.props.vm.renderer.draw();
        }
    }

    componentWillUnmount () {
        if (this._autoSaveTimer) clearTimeout(this._autoSaveTimer);
        if (this._handleVisibilityAutosave) {
            document.removeEventListener('visibilitychange', this._handleVisibilityAutosave);
        }
        if (this._handlePageHideAutosave) {
            window.removeEventListener('pagehide', this._handlePageHideAutosave);
        }
        delete window.restoreProjectForAppAutosave;
        delete window.exportProjectForAppAutosave;
    }

    componentDidMount () {
        const logStartup = (stage, extra = {}) => {
            const elapsedMs = Math.round(window.performance.now());
            console.log('[startup]', stage, {
                elapsedMs,
                module: new URLSearchParams(window.location.search).get('module') || 'default',
                ...extra
            });
        };

        logStartup('gui componentDidMount');
        // module 파라미터 없으면 리다이렉트
        const params = new URLSearchParams(window.location.search);
        if (!params.get('module')) {
            const nextUrl = new URL(window.location.href);
            nextUrl.searchParams.set('module', 'extreme');
            window.location.replace(nextUrl.toString());
            return;
        }
    setIsScratchDesktop(this.props.isScratchDesktop);
    this.props.onStorageInit(storage);
    this.props.onVmInit(this.props.vm);
    logStartup('vm initialized');
    setProjectIdMetadata(this.props.projectId);
    this.applyModuleSpriteVisibility();
    logStartup('module visibility applied');

    const module = new URLSearchParams(window.location.search).get('module') || 'default';
    const saveKey = `xrobo_autosave_${module}`;
    window.__xroboInitialProjectReady = false;
    const restoreState = window.__xroboAutosaveRestoreState || {
        source: null
    };
    window.__xroboAutosaveRestoreState = restoreState;
    this._didAttemptAutosaveRestore = false;
    this._hasRestoredAppAutosave = false;
        this._autosaveModule = module;
        this._autosaveSaveKey = saveKey;
        this._autosaveRestoreState = restoreState;
        const loadAppAutosaveProject = base64Data => {
            const binaryData = atob(base64Data);
            const arrayBuffer = new Uint8Array(binaryData.length);
            console.log('[autosave-debug] ' + JSON.stringify({
                source: 'app-native-restore-decode',
                module,
                base64Length: base64Data ? base64Data.length : 0,
                byteLength: arrayBuffer.length
            }));
            for (let i = 0; i < binaryData.length; i++) {
                arrayBuffer[i] = binaryData.charCodeAt(i);
            }
            return this.props.vm.loadProject(arrayBuffer).then(() => {
                this.applyModuleSpriteVisibility();
            });
        };
        const persistAutosave = blob => {
            console.log('[autosave-debug] ' + JSON.stringify({
                source: 'persist-autosave-start',
                module,
                saveKey,
                blobSize: blob && typeof blob.size === 'number' ? blob.size : null
            }));
            const arrayReader = new FileReader();
            arrayReader.onload = () => {
                const arrayBuffer = arrayReader.result;
                const arr = Array.from(new Uint8Array(arrayBuffer));
                safeLocalStorageSet(saveKey, JSON.stringify(arr));
                console.log('[autosave-debug] ' + JSON.stringify({
                    source: 'gui-localStorage-save',
                    module,
                    saveKey,
                    byteLength: arrayBuffer.byteLength
                }));

                if (hasAndroidInterfaceMethod('saveAutosave')) {
                    const base64Reader = new FileReader();
                    base64Reader.onload = () => {
                        const base64Data = base64Reader.result.split(',')[1];
                        console.log('[autosave-debug] ' + JSON.stringify({
                            source: 'app-native-save',
                            module,
                            byteLength: arrayBuffer.byteLength,
                            base64Length: base64Data.length
                        }));
                        callAndroidInterfaceMethod('saveAutosave', module, base64Data);
                    };
                    base64Reader.readAsDataURL(new Blob([arrayBuffer], {type: 'application/octet-stream'}));
                }

                console.log('autosave complete');
            };
            arrayReader.readAsArrayBuffer(blob);
        };
        this._persistAutosave = persistAutosave;
    window.restoreProjectForAppAutosave = base64Data => {
        if (!window.__xroboInitialProjectReady) {
            window.__xroboPendingAppAutosaveRestore = base64Data;
            console.log('[autosave-debug] ' + JSON.stringify({
                source: 'app-native-restore-queued',
                module,
                base64Length: base64Data ? base64Data.length : 0
            }));
            return Promise.resolve();
        }
        if (restoreState.source) {
            console.log('[autosave-debug] ' + JSON.stringify({
                source: 'app-native-restore-skipped',
                reason: 'restore-already-started',
                module,
                restoreSource: restoreState.source
            }));
            return Promise.resolve();
        }
        if (this._hasRestoredAppAutosave) {
            console.log('[autosave-debug] ' + JSON.stringify({
                source: 'app-native-restore-skipped',
                reason: 'already-restored',
                module
            }));
            return Promise.resolve();
        }
        this._hasRestoredAppAutosave = true;
        restoreState.source = 'app-native';
        console.log('[autosave-debug] ' + JSON.stringify({
            source: 'app-native-restore',
            module,
            base64Length: base64Data ? base64Data.length : 0
        }));
        return loadAppAutosaveProject(base64Data);
    };
    window.exportProjectForAppAutosave = () => {
        if (!hasAndroidInterfaceMethod('saveAutosave')) {
            return;
        }
        if (this._autoSaveTimer) {
            clearTimeout(this._autoSaveTimer);
            this._autoSaveTimer = null;
        }
        this.props.vm.saveProjectSb3()
            .then(blob => {
                persistAutosave(blob);
            })
            .catch(error => {
                console.log('[autosave-debug] ' + JSON.stringify({
                    source: 'app-native-save-error',
                    module,
                    message: error && error.message ? error.message : String(error)
                }));
            });
    };
    this._restoreAutosave = () => {
        if (this._didAttemptAutosaveRestore) return;
        this._didAttemptAutosaveRestore = true;

        const pendingNativeRestore = window.__xroboPendingAppAutosaveRestore;
        if (pendingNativeRestore) {
            restoreState.source = 'app-native';
            delete window.__xroboPendingAppAutosaveRestore;
            console.log('[autosave-debug] ' + JSON.stringify({
                source: 'app-native-restore',
                module,
                base64Length: pendingNativeRestore.length
            }));
            const binaryData = atob(pendingNativeRestore);
            const arrayBuffer = new Uint8Array(binaryData.length);
            for (let i = 0; i < binaryData.length; i++) {
                arrayBuffer[i] = binaryData.charCodeAt(i);
            }
            this.props.vm.loadProject(arrayBuffer).then(() => {
                this.applyModuleSpriteVisibility();
                console.log('autosave load complete');
                logStartup('autosave restore success', {source: 'app-native'});
            }).catch(e => {
                console.log('불러오기 실패:', e);
            });
            return;
        }

        const saved = safeLocalStorageGet(saveKey);
        logStartup('autosave check', {
            hasSaved: Boolean(saved),
            savedLength: saved ? saved.length : 0
        });
        if (saved) {
            restoreState.source = 'gui-localStorage';
            console.log('[autosave-debug] ' + JSON.stringify({
                source: 'gui-localStorage',
                module,
                saveKey,
                savedLength: saved.length
            }));
            logStartup('autosave restore start');
            const savedArray = JSON.parse(saved);
            const savedBytes = new Uint8Array(savedArray);
            console.log('[autosave-debug] ' + JSON.stringify({
                source: 'gui-localStorage-restore-decode',
                module,
                saveKey,
                itemCount: savedArray.length,
                byteLength: savedBytes.byteLength
            }));
            this.props.vm.loadProject(
                savedBytes.buffer
            ).then(() => {
                this.applyModuleSpriteVisibility();
                console.log('autosave load complete');
                logStartup('autosave restore success', {source: 'gui-localStorage'});
            }).catch(e => {
                console.log('autosave load failed:', e);
            });
        }
    };
    const flushAutosave = reason => {
        if (this._autoSaveTimer) {
            clearTimeout(this._autoSaveTimer);
            this._autoSaveTimer = null;
        }
        this.props.vm.saveProjectSb3().then(blob => {
            console.log('[autosave-debug] ' + JSON.stringify({
                source: 'flush-autosave',
                module,
                reason
            }));
            persistAutosave(blob);
        }).catch(error => {
            console.log('[autosave-debug] ' + JSON.stringify({
                source: 'flush-autosave-error',
                module,
                reason,
                message: error && error.message ? error.message : String(error)
            }));
        });
    };
    this._handleVisibilityAutosave = () => {
        if (document.visibilityState === 'hidden') {
            flushAutosave('visibilitychange');
        }
    };
    this._handlePageHideAutosave = () => flushAutosave('pagehide');
    document.addEventListener('visibilitychange', this._handleVisibilityAutosave);
    window.addEventListener('pagehide', this._handlePageHideAutosave);

    // 자동저장 (PROJECT_CHANGED 이벤트 + 2초 디바운스)
    this.props.vm.on('PROJECT_CHANGED', () => {
        if (this._autoSaveTimer) clearTimeout(this._autoSaveTimer);
        this._autoSaveTimer = setTimeout(() => {
            this.props.vm.saveProjectSb3().then(blob => {
                persistAutosave(blob);
            });
        }, 2000);
    });

    // 자동저장(프로젝트 로드될 때마다 즉시 저장 : 새로 만들기도 포함)
    this.props.vm.on('PROJECT_LOADED', () => {
        console.log('PROJECT_LOADED 발생!!!')
        logStartup('PROJECT_LOADED event');
        this.applyModuleSpriteVisibility();
});
}
    componentDidUpdate (prevProps) {
        if (this.props.locale !== prevProps.locale) {
            this.applyModuleSpriteVisibility();
        }
        if (this.props.projectId !== prevProps.projectId) {
            if (this.props.projectId !== null) {
                this.props.onUpdateProjectId(this.props.projectId);
            }
            setProjectIdMetadata(this.props.projectId);
        }
        if (this.props.isShowingProject && !prevProps.isShowingProject) {
            window.__xroboInitialProjectReady = true;
            if (this._autosaveRestoreState && this._autosaveRestoreState.source) {
                console.log('[autosave-debug] ' + JSON.stringify({
                    source: 'gui-localStorage-skipped',
                    reason: 'restore-already-started',
                    module: this._autosaveModule,
                    saveKey: this._autosaveSaveKey,
                    restoreSource: this._autosaveRestoreState.source
                }));
            } else if (this._restoreAutosave) {
                this._restoreAutosave();
            }
            // this only notifies container when a project changes from not yet loaded to loaded
            // At this time the project view in www doesn't need to know when a project is unloaded
            console.log('[startup]', 'project visible', {
                elapsedMs: Math.round(window.performance.now()),
                module: new URLSearchParams(window.location.search).get('module') || 'default'
            });
            this.props.onProjectLoaded();
        }
    }
    render () {
        if (this.props.isError) {
            throw new Error(
                `Error in Scratch GUI [location=${window.location}]: ${this.props.error}`);
        }
        const {
            /* eslint-disable no-unused-vars */
            assetHost,
            cloudHost,
            error,
            isError,
            isScratchDesktop,
            isShowingProject,
            onProjectLoaded,
            onStorageInit,
            onUpdateProjectId,
            onVmInit,
            projectHost,
            projectId,
            /* eslint-enable no-unused-vars */
            children,
            fetchingProject,
            isLoading,
            loadingStateVisible,
            ...componentProps
        } = this.props;
        return (
            <GUIComponent
                loading={fetchingProject || isLoading || loadingStateVisible}
                {...componentProps}
            >
                {children}
            </GUIComponent>
        );
    }
}

GUI.propTypes = {
    assetHost: PropTypes.string,
    children: PropTypes.node,
    cloudHost: PropTypes.string,
    error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    fetchingProject: PropTypes.bool,
    intl: intlShape,
    isError: PropTypes.bool,
    isLoading: PropTypes.bool,
    isScratchDesktop: PropTypes.bool,
    isShowingProject: PropTypes.bool,
    isTotallyNormal: PropTypes.bool,
    locale: PropTypes.string,
    loadingStateVisible: PropTypes.bool,
    onProjectLoaded: PropTypes.func,
    onSeeCommunity: PropTypes.func,
    onStorageInit: PropTypes.func,
    onUpdateProjectId: PropTypes.func,
    onVmInit: PropTypes.func,
    projectHost: PropTypes.string,
    projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    telemetryModalVisible: PropTypes.bool,
    vm: PropTypes.instanceOf(VM).isRequired
};

GUI.defaultProps = {
    isScratchDesktop: false,
    isTotallyNormal: false,
    onStorageInit: storageInstance => storageInstance.addOfficialScratchWebStores(),
    onProjectLoaded: () => {},
    onUpdateProjectId: () => {},
    onVmInit: (/* vm */) => {}
};

const mapStateToProps = state => {
    const loadingState = state.scratchGui.projectState.loadingState;
    return {
        activeTabIndex: state.scratchGui.editorTab.activeTabIndex,
        alertsVisible: state.scratchGui.alerts.visible,
        backdropLibraryVisible: state.scratchGui.modals.backdropLibrary,
        blocksTabVisible: state.scratchGui.editorTab.activeTabIndex === BLOCKS_TAB_INDEX,
        cardsVisible: state.scratchGui.cards.visible,
        connectionModalVisible: state.scratchGui.modals.connectionModal,
        costumeLibraryVisible: state.scratchGui.modals.costumeLibrary,
        costumesTabVisible: state.scratchGui.editorTab.activeTabIndex === COSTUMES_TAB_INDEX,
        error: state.scratchGui.projectState.error,
        fontsLoaded: state.scratchGui.fontsLoaded,
        isError: getIsError(loadingState),
        isFullScreen: state.scratchGui.mode.isFullScreen,
        isPlayerOnly: state.scratchGui.mode.isPlayerOnly,
        locale: state.locales.locale,
        isRtl: state.locales.isRtl,
        isShowingProject: getIsShowingProject(loadingState),
        loadingStateVisible: state.scratchGui.modals.loadingProject,
        projectId: state.scratchGui.projectState.projectId,
        soundsTabVisible: state.scratchGui.editorTab.activeTabIndex === SOUNDS_TAB_INDEX,
        targetIsStage: (
            state.scratchGui.targets.stage &&
            state.scratchGui.targets.stage.id === state.scratchGui.targets.editingTarget
        ),
        telemetryModalVisible: state.scratchGui.modals.telemetryModal,
        tipsLibraryVisible: state.scratchGui.modals.tipsLibrary,
        vm: state.scratchGui.vm,

    };
};

const mapDispatchToProps = dispatch => ({
    onExtensionButtonClick: () => dispatch(openExtensionLibrary()),
    onActivateTab: tab => dispatch(activateTab(tab)),
    onActivateCostumesTab: () => dispatch(activateTab(COSTUMES_TAB_INDEX)),
    onActivateSoundsTab: () => dispatch(activateTab(SOUNDS_TAB_INDEX)),
    onRequestCloseBackdropLibrary: () => dispatch(closeBackdropLibrary()),
    onRequestCloseCostumeLibrary: () => dispatch(closeCostumeLibrary()),
    onRequestCloseTelemetryModal: () => dispatch(closeTelemetryModal()),

});

const ConnectedGUI = injectIntl(connect(
    mapStateToProps,
    mapDispatchToProps
)(GUI));

// note that redux's 'compose' function is just being used as a general utility to make
// the hierarchy of HOC constructor calls clearer here; it has nothing to do with redux's
// ability to compose reducers.
const WrappedGui = compose(
    LocalizationHOC,
    ErrorBoundaryHOC('Top Level App'),
    FontLoaderHOC,
    QueryParserHOC,
    ProjectFetcherHOC,
    TitledHOC,
    ProjectSaverHOC,
    vmListenerHOC,
    vmManagerHOC,
    SBFileUploaderHOC,
    cloudManagerHOC,
    systemPreferencesHOC
)(ConnectedGUI);

WrappedGui.setAppElement = ReactModal.setAppElement;
export default WrappedGui;
